import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import React, { useEffect, useState } from 'react';
import { useGet } from '../../network/useGet';
import { putToHub } from '../../network/actions';
import { useNotificationContext } from '../../context/NotificationContext';
import { useZxing } from 'react-zxing';

const OnboardTV = () => {
  const [scanResult, setScanResult] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { data: deviceGroupsData } = useGet('devices/groups');

  let deviceGroups = [];
  if(deviceGroupsData){
    deviceGroups = deviceGroupsData.map(group => ({ name: group.name, value: group.name, id: group.id }));
  }

  const handleScan = (data) => {
    if (data) {
      setScanResult(JSON.parse(data.text));
      setIsScanning(false);
    }
  };

  const handleError = (error) => {
    if (error.name === 'NotAllowedError') {
      setErrorMessage('Camera access denied. Please allow camera permissions.');
      setIsScanning(false);
    } 
  };

  const toggleScanning = () => {
    setScanResult('');
    setIsScanning(isScanning => !isScanning);
  };

  return (
    <div>
      {errorMessage ? (
        <Info message={errorMessage} className="bg-red-300"/>
      ):(
        <Button onClick={toggleScanning} label={isScanning ? 'Stop Scanning': 'Scan QR Code'} />
      )}
      {isScanning && (
        <QRCodeScanner handleScan={handleScan} handleError={handleError}/>
      )}
      
      {scanResult && (
        <div className='mt-4'>
          <p><b>IP Address:</b> {scanResult.ip || "Not Found"}</p>
          <p><b>MAC Address:</b> {scanResult.mac || "Not Found"}</p>
          <OnboardingForm deviceGroups={deviceGroups} scanResult={scanResult}/>
        </div>
      )}
    </div>
  );
};

export default OnboardTV;

const QRCodeScanner = ({handleScan, handleError}) => {

  const {ref} = useZxing({
    onDecodeResult: (result)=>handleScan(result),
    onError: (error)=>handleError(error)
  });

  return(
    <video ref={ref} className='mt-4'/>
  )
};

const OnboardingForm = ({deviceGroups, scanResult}) => {

  const params = new URLSearchParams(scanResult).toString();
  const url = `devices/getOrCreate?${params}`;

  const [reloadCount, setReloadCount] = useState(0);
  const { isLoading: isDevicesLoading, data: device } = useGet(url, reloadCount);

  const [formData, setFormData] = useState({
    ip: scanResult.ip,
    mac: scanResult.mac,
    deck: "",
    location: "",
    group: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { toast } = useNotificationContext();

  useEffect(()=>{
    if(device){
      setFormData(formData => ({
        ...formData,
        deck: device.deck || "",
        location: device.location || "",
        group: device.deviceGroup?.name || "",
      }));
    }
  },[device]);

  const onChangeFormField = (event) => {
    const name = event.target ? event.target.name : event.originalEvent.target.name;
    const value = event.target ? event.target.value : event.value;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = () => {
    return Object.keys(formData).every(
      key => key === "ip" || key === "mac" || key === "deck" || formData[key] !== ""
    );
  }

  const saveData = async() => {
    setIsLoading(true);
       
    const isValid = validateForm();

    if(isValid){
      setErrorMessage("");
      let payload={
        deck: formData.deck,
        location: formData.location,
        deviceGroupId: deviceGroups.find(group => group?.name === formData.group)?.id,
      };

      const successCallback = () => {
        toast.current.show({severity: 'success', summary: 'Details Updated Successfully'});
      };

      const errorCallback = () => {
        toast.current.show({severity: 'error', summary: 'Failed to Update'});
      }
      await putToHub(`devices/${device.id}`, payload, successCallback, errorCallback);
    }else{
      setErrorMessage("Please Enter All the details");
    }
    setIsLoading(false);
  }

  const reset = () => {
    setErrorMessage("");
    setReloadCount(reloadCount => reloadCount + 1);
  }

  if(isDevicesLoading){
    return <Info message="Fetching Device Details.." />;
  }

  if(!device){
    return  <Info message={"Device Not found"} />;
  }

  if(device && (device?.length > 1 || device?.length === 0)){
    return <Info message={`${device.length === 0 ? "Device Not Found" : "IP address conflict between multiple Devices"}`} />;
  }

  return(
    <div className="px-4 py-4 my-4 border-t-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/*MAC Address*/}
      <div>
        <label htmlFor="ip">MAC address</label>
        <div>
          <InputText
            id="mac"
            name="mac"
            value={formData.mac}
            className="w-9/12 max-w-9/12"
            disabled={true}
          />
        </div>
      </div>

      {/*IP Address*/}
      <div>
        <label htmlFor="ip">IP Address</label>
        <div>
          <InputText
            id="ip"
            name="ip"
            value={formData.ip}
            className="w-9/12 max-w-9/12"
            disabled={true}
          />
        </div>
      </div>

      {/*Name*/}
      <div>
        <label htmlFor="deck">Deck</label>
        <div>
          <InputText
            id="deck"
            name="deck"
            value={formData.deck}
            onChange={onChangeFormField}
            className="w-9/12 max-w-9/12"
            disabled={isLoading}
          />
        </div>
      </div>

      {/* Group */}
      <div>
        <label htmlFor="state">Group</label>
        <div>
          <Dropdown
            id="group"
            name="group"
            value={formData.group}
            onChange={onChangeFormField}
            options={deviceGroups}
            optionLabel="name"
            optionValue="value"
            className="w-9/12 max-w-9/12"
            disabled={isLoading}
            placeholder="-- Select --"
          />
        </div>
      </div>

      {/*Location*/}
      <div>
        <label htmlFor="location">Location</label>
        <div>
          <InputText
            id="location"
            name="location"
            value={formData.location}
            onChange={onChangeFormField}
            className="w-9/12 max-w-9/12"
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2 self-end">
      {errorMessage && <p className='text-red-700'>{errorMessage}</p>}
        <div className='flex gap-2 md:gap-6 justify-start items-end'>
          <Button
            label="Save"
            onClick={() => saveData()}
            disabled={isLoading}
            />
          {/* Reset */}
          <Button label="Reset" onClick={reset} disabled={isLoading} />
        </div>
      </div>
    </div>
  )
}

const Info = ({message, className}) => {
  return(
    <div className={`px-4 py-4 my-4 border text-center bg-gray-300 ${className}`}>{message}</div>
  )
}